import React, { useState, useEffect } from 'react';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { AdCard, useAds } from '../../components/ads/AdCard';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { TrendingUp, Clock, DollarSign, Target, AlertCircle, Wallet, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const GrowthPackagesPage = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [selectedPaymentCurrency, setSelectedPaymentCurrency] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const { ads, loading: adsLoading } = useAds('investments');
  const navigate = useNavigate();

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/growth-packages`, {
        credentials: 'include'
      });
      const data = await response.json();
      setPackages(data.packages || []);
    } catch (error) {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyClick = async (pkg) => {
    setSelectedPackage(pkg);
    
    // Fetch payment options
    try {
      const response = await fetch(`${API_URL}/api/user/growth-packages/${pkg.id}/payment-options`, {
        credentials: 'include'
      });
      const data = await response.json();
      setPaymentOptions(data.options || []);
      
      // Auto-select primary currency if sufficient
      const primaryOption = data.options.find(o => o.is_primary && o.sufficient);
      if (primaryOption) {
        setSelectedPaymentCurrency(primaryOption.currency);
      } else {
        // Select first sufficient option
        const sufficientOption = data.options.find(o => o.sufficient);
        setSelectedPaymentCurrency(sufficientOption?.currency || data.options[0]?.currency);
      }
      
      setShowPaymentDialog(true);
    } catch (error) {
      toast.error('Failed to load payment options');
    }
  };

  const handlePurchase = async () => {
    if (!selectedPaymentCurrency) {
      toast.error('Please select a payment method');
      return;
    }
    
    const selectedOption = paymentOptions.find(o => o.currency === selectedPaymentCurrency);
    if (!selectedOption || !selectedOption.sufficient) {
      toast.error(`Insufficient ${selectedPaymentCurrency} balance`);
      return;
    }
    
    setPurchasing(selectedPackage.id);
    try {
      const url = selectedPaymentCurrency === selectedPackage.currency
        ? `${API_URL}/api/user/growth-packages/${selectedPackage.id}/purchase`
        : `${API_URL}/api/user/growth-packages/${selectedPackage.id}/purchase?payment_currency=${selectedPaymentCurrency}`;
      
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Purchase failed');
      }
      
      toast.success('Package purchased successfully!');
      setShowPaymentDialog(false);
      navigate('/user/my-growth-packages');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setPurchasing(null);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      starter: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
      bronze: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      silver: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200',
      gold: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      platinum: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      vip: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };
    return colors[category] || colors.starter;
  };

  return (
    <FocusedLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Growth Packages</h1>
          <p className="text-muted-foreground">Fixed daily earnings packages with guaranteed payouts</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading packages...</div>
        ) : packages.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <TrendingUp className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No packages available at the moment</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {packages.map((pkg) => (
              <Card key={pkg.id} className="relative overflow-hidden hover:shadow-lg transition-shadow">
                {pkg.badge && (
                  <div className="absolute top-4 right-4">
                    <Badge variant="destructive" className="uppercase text-xs">{pkg.badge}</Badge>
                  </div>
                )}
                
                <CardContent className="pt-6">
                  <div className="text-center mb-4">
                    <div className="text-5xl mb-2">{pkg.icon}</div>
                    <h3 className="text-xl font-bold">{pkg.name}</h3>
                    <Badge className={getCategoryColor(pkg.category) + ' mt-2'}>
                      {pkg.category.toUpperCase()}
                    </Badge>
                  </div>

                  <p className="text-sm text-muted-foreground text-center mb-4">
                    {pkg.short_description}
                  </p>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <span className="text-sm text-muted-foreground">Package Price</span>
                      <span className="font-bold text-lg">{pkg.currency} {pkg.price}</span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <span className="text-sm">Daily Earnings</span>
                      <span className="font-bold text-green-600 dark:text-green-400">
                        {pkg.currency} {pkg.daily_payout}
                      </span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        Duration
                      </span>
                      <span className="font-bold">{pkg.duration_days} Days</span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <span className="text-sm">Total Earnings</span>
                      <span className="font-bold text-blue-600 dark:text-blue-400">
                        {pkg.currency} {pkg.total_earnings}
                      </span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <span className="text-sm flex items-center gap-1">
                        <Target className="h-4 w-4" />
                        ROI
                      </span>
                      <span className="font-bold text-purple-600 dark:text-purple-400">
                        {pkg.roi_percentage.toFixed(0)}%
                      </span>
                    </div>
                  </div>

                  {pkg.user_limit_reached ? (
                    <Button disabled className="w-full" variant="outline">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      Limit Reached
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => handleBuyClick(pkg)} 
                      disabled={purchasing === pkg.id}
                      className="w-full"
                    >
                      <Wallet className="h-4 w-4 mr-2" />
                      {purchasing === pkg.id ? 'Processing...' : 'Buy Package'}
                    </Button>
                  )}

                  <p className="text-xs text-muted-foreground text-center mt-2">
                    Withdrawal fee: {pkg.withdrawal_fee_percentage}% • Min: {pkg.currency} {pkg.min_withdrawal_amount}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Ads Section (bottom) */}
        {!adsLoading && ads.length > 0 && (
          <div className="space-y-4 mt-8">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="h-4 w-4" />
              <span>Sponsored Investment Opportunities</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ads.slice(0, 3).map((ad) => (
                <AdCard key={ad.id} ad={ad} />
              ))}
            </div>
          </div>
        )}

        {/* Payment Options Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Choose Payment Method</DialogTitle>
            </DialogHeader>
            
            {selectedPackage && (
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{selectedPackage.name}</span>
                    <Badge>{selectedPackage.category}</Badge>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    Package Price: {selectedPackage.currency} {selectedPackage.price}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Select Payment Currency</Label>
                  {paymentOptions.map((option) => (
                    <div
                      key={option.currency}
                      onClick={() => option.sufficient && setSelectedPaymentCurrency(option.currency)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedPaymentCurrency === option.currency
                          ? 'border-primary bg-primary/5'
                          : option.sufficient
                          ? 'border-border hover:border-primary/50'
                          : 'border-border opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Wallet className="h-5 w-5" />
                          <div>
                            <div className="font-medium">{option.currency}</div>
                            <div className="text-xs text-muted-foreground">
                              Balance: {option.user_balance.toFixed(4)} {option.currency}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold">
                            {option.amount_needed.toFixed(4)} {option.currency}
                          </div>
                          {option.is_primary ? (
                            <Badge variant="outline" className="text-xs">Primary</Badge>
                          ) : (
                            <div className="text-xs text-muted-foreground">
                              Rate: {option.exchange_rate?.toFixed(4)}
                            </div>
                          )}
                        </div>
                      </div>
                      {!option.sufficient && (
                        <div className="mt-2 text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          Insufficient balance
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handlePurchase}
                disabled={!selectedPaymentCurrency || purchasing}
              >
                {purchasing ? 'Processing...' : 'Confirm Purchase'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </FocusedLayout>
  );
};